import { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  X, FileText, Send, Download, Save, 
  Building2, User, Calendar, MapPin, Euro, Clock, Plus, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const LettreMissionForm = ({ formateur, centreProfile, user, onClose, onSuccess }) => {
  const [saving, setSaving] = useState(false);
  
  // Dates multiples
  const [periodes, setPeriodes] = useState([{ date_debut: '', date_fin: '' }]);
  
  // État du formulaire
  const [formData, setFormData] = useState({
    // Infos Centre (pré-remplies)
    centre_id: centreProfile?.site_id || 'atlantique',
    centre_nom: `FISL ${centreProfile?.site_name || 'Atlantique'}`,
    centre_forme_juridique: centreProfile?.forme_juridique || 'SARL',
    centre_adresse: centreProfile ? `${centreProfile.adresse || ''}, ${centreProfile.code_postal || ''} ${centreProfile.ville || ''}`.trim() : '',
    centre_representant: user?.prenom && user?.nom ? `${user.prenom} ${user.nom}` : '',
    centre_representant_fonction: 'Directeur',
    centre_siret: centreProfile?.siret || '',
    centre_ape: centreProfile?.ape || '',
    centre_numero_activite: centreProfile?.numero_activite || '',
    
    // Infos Formateur (pré-remplies)
    formateur_id: formateur?.user_id || '',
    formateur_nom: formateur?.nom || '',
    formateur_prenom: formateur?.prenom || '',
    formateur_statut: 'Formateur indépendant',
    formateur_adresse: formateur?.profile?.adresse || '',
    formateur_siret: formateur?.profile?.siret || '',
    
    // Champs à remplir manuellement
    intitule_formation: 'BPJEPS Motonautisme et Disciplines Associées',
    objectif_1: '',
    objectif_2: '',
    objectif_3: '',
    duree: '',
    lieu: '',
    // Montant de la prestation
    type_tarif: 'heure',
    tarif_unitaire: '',
    nombre_unites: '',
    montant_total: '',
    modalite_paiement: 'à l\'issue de la formation / sur facture',
    preavis_jours: '15',
    lieu_signature: centreProfile?.ville || '',
    
    // Signatures
    signature_centre: null,
    tampon_centre: centreProfile?.tampon_img || null
  });

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      if (field === 'tarif_unitaire' || field === 'nombre_unites') {
        const tarif = parseFloat(field === 'tarif_unitaire' ? value : prev.tarif_unitaire) || 0;
        const nombre = parseFloat(field === 'nombre_unites' ? value : prev.nombre_unites) || 0;
        newData.montant_total = (tarif * nombre).toFixed(2);
      }
      return newData;
    });
  };

  // Gestion des périodes de dates
  const addPeriode = () => {
    setPeriodes([...periodes, { date_debut: '', date_fin: '' }]);
  };

  const removePeriode = (index) => {
    if (periodes.length > 1) {
      setPeriodes(periodes.filter((_, i) => i !== index));
    }
  };

  const updatePeriode = (index, field, value) => {
    const newPeriodes = [...periodes];
    newPeriodes[index][field] = value;
    setPeriodes(newPeriodes);
  };

  // Formater les dates pour l'envoi
  const formatDates = () => {
    return periodes
      .filter(p => p.date_debut || p.date_fin)
      .map(p => {
        if (p.date_debut && p.date_fin) {
          return `Du ${p.date_debut} au ${p.date_fin}`;
        } else if (p.date_debut) {
          return `Le ${p.date_debut}`;
        }
        return '';
      })
      .filter(d => d)
      .join(' | ');
  };

  const handleSave = async (sendAfter = false) => {
    if (!formData.formateur_id) {
      toast.error('Veuillez sélectionner un formateur');
      return;
    }

    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        dates: formatDates(),
        periodes: periodes
      };
      
      const response = await axios.post('/lettres-mission', dataToSend);
      const lettreId = response.data.lettre_id;
      
      if (sendAfter) {
        await axios.post(`/lettres-mission/${lettreId}/envoyer`);
        toast.success('Lettre de mission créée et envoyée au formateur');
      } else {
        toast.success('Lettre de mission enregistrée');
      }
      
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!formData.formateur_id) {
      toast.error('Veuillez d\'abord enregistrer la lettre');
      return;
    }
    
    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        dates: formatDates(),
        periodes: periodes
      };
      
      const response = await axios.post('/lettres-mission', dataToSend);
      const lettreId = response.data.lettre_id;
      
      // Télécharger le PDF
      const pdfResponse = await axios.get(`/lettres-mission/${lettreId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([pdfResponse.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${formData.formateur_nom}_${formData.formateur_prenom}_lettre_mission.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF téléchargé');
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error('Erreur lors du téléchargement du PDF');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="lettre-mission-form">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#0F172A]">Lettre de Mission</h2>
              <p className="text-sm text-slate-500">
                Pour {formateur?.prenom} {formateur?.nom}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Contenu */}
        <div className="p-6 space-y-6">
          {/* Section Centre */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 flex items-center gap-2 mb-4">
              <Building2 className="h-4 w-4" />
              Informations du Centre (Client)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nom de l'organisme</Label>
                <Input 
                  value={formData.centre_nom}
                  onChange={(e) => handleChange('centre_nom', e.target.value)}
                />
              </div>
              <div>
                <Label>Forme juridique</Label>
                <Input 
                  value={formData.centre_forme_juridique}
                  onChange={(e) => handleChange('centre_forme_juridique', e.target.value)}
                  placeholder="SARL, SAS..."
                />
              </div>
              <div className="md:col-span-2">
                <Label>Adresse complète</Label>
                <Input 
                  value={formData.centre_adresse}
                  onChange={(e) => handleChange('centre_adresse', e.target.value)}
                />
              </div>
              <div>
                <Label>Représentant</Label>
                <Input 
                  value={formData.centre_representant}
                  onChange={(e) => handleChange('centre_representant', e.target.value)}
                />
              </div>
              <div>
                <Label>Fonction</Label>
                <Input 
                  value={formData.centre_representant_fonction}
                  onChange={(e) => handleChange('centre_representant_fonction', e.target.value)}
                  placeholder="Directeur, Gérant..."
                />
              </div>
            </div>
          </div>

          {/* Section Formateur */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-800 flex items-center gap-2 mb-4">
              <User className="h-4 w-4" />
              Informations du Formateur
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nom</Label>
                <Input 
                  value={formData.formateur_nom}
                  disabled
                  className="bg-slate-100"
                />
              </div>
              <div>
                <Label>Prénom</Label>
                <Input 
                  value={formData.formateur_prenom}
                  disabled
                  className="bg-slate-100"
                />
              </div>
              <div>
                <Label>Statut</Label>
                <select
                  value={formData.formateur_statut}
                  onChange={(e) => handleChange('formateur_statut', e.target.value)}
                  className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md"
                >
                  <option value="Formateur indépendant">Formateur indépendant</option>
                  <option value="Salarié">Salarié</option>
                  <option value="Prestataire">Prestataire</option>
                </select>
              </div>
              <div>
                <Label>N° SIRET (le cas échéant)</Label>
                <Input 
                  value={formData.formateur_siret}
                  onChange={(e) => handleChange('formateur_siret', e.target.value)}
                  placeholder="123 456 789 00012"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Adresse</Label>
                <Input 
                  value={formData.formateur_adresse}
                  onChange={(e) => handleChange('formateur_adresse', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Section Mission */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h3 className="font-semibold text-amber-800 flex items-center gap-2 mb-4">
              <FileText className="h-4 w-4" />
              Détails de la Mission
            </h3>
            <div className="space-y-4">
              <div>
                <Label>Intitulé de la formation</Label>
                <Input 
                  value={formData.intitule_formation}
                  onChange={(e) => handleChange('intitule_formation', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Objectifs de compétences</Label>
                <Input 
                  value={formData.objectif_1}
                  onChange={(e) => handleChange('objectif_1', e.target.value)}
                  placeholder="[Objectif 1]"
                  className="mb-2"
                />
                <Input 
                  value={formData.objectif_2}
                  onChange={(e) => handleChange('objectif_2', e.target.value)}
                  placeholder="[Objectif 2]"
                  className="mb-2"
                />
                <Input 
                  value={formData.objectif_3}
                  onChange={(e) => handleChange('objectif_3', e.target.value)}
                  placeholder="[Objectif 3]"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-1">
                    <Clock className="h-3 w-3" /> Durée
                  </Label>
                  <Input 
                    value={formData.duree}
                    onChange={(e) => handleChange('duree', e.target.value)}
                    placeholder="X heures / X jours"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" /> Lieu
                  </Label>
                  <Input 
                    value={formData.lieu}
                    onChange={(e) => handleChange('lieu', e.target.value)}
                    placeholder="Présentiel / Distanciel + lieu"
                  />
                </div>
              </div>

              {/* Dates multiples */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> Périodes d'intervention
                  </Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addPeriode}
                    className="text-green-600 border-green-300 hover:bg-green-50"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter une période
                  </Button>
                </div>
                <div className="space-y-2">
                  {periodes.map((periode, index) => (
                    <div key={index} className="flex items-center gap-2 bg-white p-2 rounded border border-slate-200">
                      <span className="text-sm text-slate-500 w-8">Du</span>
                      <Input
                        type="date"
                        value={periode.date_debut}
                        onChange={(e) => updatePeriode(index, 'date_debut', e.target.value)}
                        className="flex-1"
                      />
                      <span className="text-sm text-slate-500 w-8">au</span>
                      <Input
                        type="date"
                        value={periode.date_fin}
                        onChange={(e) => updatePeriode(index, 'date_fin', e.target.value)}
                        className="flex-1"
                      />
                      {periodes.length > 1 && (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => removePeriode(index)}
                          className="text-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Section Montant de la prestation */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h3 className="font-semibold text-purple-800 flex items-center gap-2 mb-4">
              <Euro className="h-4 w-4" />
              Montant de la prestation
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>Type de tarif</Label>
                <select
                  value={formData.type_tarif}
                  onChange={(e) => handleChange('type_tarif', e.target.value)}
                  className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md"
                >
                  <option value="heure">Par heure</option>
                  <option value="journee">Par journée</option>
                </select>
              </div>
              <div>
                <Label>Tarif unitaire (€)</Label>
                <Input 
                  value={formData.tarif_unitaire}
                  onChange={(e) => handleChange('tarif_unitaire', e.target.value)}
                  placeholder="50"
                  type="number"
                />
              </div>
              <div>
                <Label>Nombre de {formData.type_tarif === 'heure' ? 'heures' : 'journées'}</Label>
                <Input 
                  value={formData.nombre_unites}
                  onChange={(e) => handleChange('nombre_unites', e.target.value)}
                  placeholder="10"
                  type="number"
                />
              </div>
              <div>
                <Label>Montant total (€ TTC)</Label>
                <Input 
                  value={formData.montant_total}
                  onChange={(e) => handleChange('montant_total', e.target.value)}
                  className="bg-green-50 font-semibold"
                  readOnly
                />
              </div>
            </div>
            <div className="mt-4">
              <Label>Modalités de paiement</Label>
              <Input 
                value={formData.modalite_paiement}
                onChange={(e) => handleChange('modalite_paiement', e.target.value)}
              />
            </div>
          </div>

          {/* Section Signature */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <h3 className="font-semibold text-slate-800 mb-4">Lieu et Date de Signature</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Lieu</Label>
                <Input 
                  value={formData.lieu_signature}
                  onChange={(e) => handleChange('lieu_signature', e.target.value)}
                />
              </div>
              <div>
                <Label>Préavis de résiliation (jours)</Label>
                <Input 
                  value={formData.preavis_jours}
                  onChange={(e) => handleChange('preavis_jours', e.target.value)}
                  type="number"
                />
              </div>
            </div>
            
            {formData.tampon_centre && (
              <div className="mt-4">
                <Label>Tampon/Signature du Centre</Label>
                <div className="mt-2 border border-slate-200 rounded-lg p-2 bg-white inline-block">
                  <img 
                    src={formData.tampon_centre} 
                    alt="Tampon du centre" 
                    className="max-h-24 object-contain"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer avec actions */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 flex flex-wrap gap-3 justify-end">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button 
            variant="outline" 
            onClick={handleDownloadPdf}
            disabled={saving}
            className="text-blue-600 border-blue-200 hover:bg-blue-50"
          >
            <Download className="h-4 w-4 mr-2" />
            Télécharger PDF
          </Button>
          <Button 
            variant="outline"
            onClick={() => handleSave(false)}
            disabled={saving}
          >
            <Save className="h-4 w-4 mr-2" />
            Enregistrer
          </Button>
          <Button 
            onClick={() => handleSave(true)}
            disabled={saving}
            className="bg-[#8DC63F] hover:bg-[#7db336]"
          >
            <Send className="h-4 w-4 mr-2" />
            {saving ? 'Envoi...' : 'Envoyer au formateur'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LettreMissionForm;
